import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../../lib/AuthContext';
import { supabase } from '../../../lib/supabase';
import Avatar from '../display/Avatar';

interface Workspace {
  workspace_id: string;
  workspace_name: string;
  workspace_type: string;
  logo_url: string | null;
}

export const UserProfileDropdown = () => {
  const { user } = useAuth();
  const [showWorkspaceMenu, setShowWorkspaceMenu] = useState(false);
  const workspaceRef = useRef<HTMLDivElement>(null);
  const [teamWorkspaces, setTeamWorkspaces] = useState<Workspace[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (workspaceRef.current && !workspaceRef.current.contains(event.target as Node)) {
        setShowWorkspaceMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchWorkspaces = async () => {
    if (!user) return;

    // Fetch team workspaces directly with a single query
    const { data: workspaces, error: workspacesError } = await supabase
      .from('workspaces')
      .select('workspace_id, workspace_name, workspace_type, logo_url')
      .eq('workspace_type', 'team')
      .in('workspace_id', (
        await supabase
          .from('workspace_users')
          .select('workspace_id')
          .eq('user_id', user.id)
      ).data?.map(row => row.workspace_id) || []
      );

    if (workspacesError) {
      console.error('Error fetching workspaces:', workspacesError);
      return;
    }

    setTeamWorkspaces(workspaces || []);
  };

  useEffect(() => {
    fetchWorkspaces();
  }, [user]);

  // Add new effect to listen for workspace updates
  useEffect(() => {
    const handleWorkspaceUpdate = () => {
      fetchWorkspaces();
    };

    window.addEventListener('workspaceUpdated', handleWorkspaceUpdate);

    return () => {
      window.removeEventListener('workspaceUpdated', handleWorkspaceUpdate);
    };
  }, [user]); // Include user in dependencies since fetchWorkspaces uses it

  if (!user) return null;

  return (
    <div className="relative" ref={workspaceRef}>
      <button
        onClick={() => setShowWorkspaceMenu(!showWorkspaceMenu)}
        className={`flex items-center gap-3 px-4 py-2 rounded-full border transition-colors cursor-pointer ${
          showWorkspaceMenu
            ? 'bg-primary-50 border-primary-200'
            : 'bg-white border-gray-200 hover:bg-gray-50'
        }`}
      >
        <div className="flex items-center gap-3">
          <Avatar
            firstName={user.user_metadata?.first_name || 'Guest'}
            lastName={user.user_metadata?.last_name || ''}
            profileImageUrl={user.user_metadata?.profile_image_url}
            size="small"
          />
          <span className="text-gray-900 font-medium truncate max-w-[200px]">
            {user.user_metadata?.first_name} {user.user_metadata?.last_name}
          </span>
        </div>
        <ChevronDownIcon
          className={`w-5 h-5 text-gray-500 transition-transform ${
            showWorkspaceMenu ? 'rotate-180' : ''
          }`}
        />
      </button>

      {showWorkspaceMenu && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-custom py-4 z-20">
          <div className="text-center px-4">
            <div className="text-sm text-gray-500 mb-4">{user.email}</div>

            <div className="mb-4">
              <button 
                onClick={() => {
                  navigate('/profile');
                  setShowWorkspaceMenu(false);
                }}
                className="inline-block cursor-pointer"
              >
                <Avatar
                  firstName={user.user_metadata?.first_name || 'Guest'}
                  lastName={user.user_metadata?.last_name || ''}
                  profileImageUrl={user.user_metadata?.profile_image_url}
                  size="large"
                  className="mx-auto mb-2 hover:opacity-80 transition-opacity"
                />
              </button>
              <div className="text-lg font-medium text-gray-900 mb-1 truncate max-w-[250px] mx-auto">
                {user.user_metadata?.first_name} {user.user_metadata?.last_name}
              </div>
            </div>

            <div className="border-t pt-4">
              <div className="text-sm font-medium text-gray-900 mb-3">Your Team Workspaces</div>
              <div className="space-y-2">
                {teamWorkspaces.length > 0 ? (
                  teamWorkspaces.map((workspace) => (
                    <button
                      key={workspace.workspace_id}
                      onClick={() => navigate(`/workspace/${workspace.workspace_id}`)}
                      className="w-full px-3 py-2 text-left hover:bg-gray-50 rounded-lg cursor-pointer"
                    >
                      <div className="flex items-center gap-2">
                        {workspace.logo_url ? (
                          <img
                            src={workspace.logo_url}
                            alt={workspace.workspace_name}
                            className="w-6 h-6 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-6 h-6 rounded-full bg-primary-100 flex items-center justify-center">
                            <span className="text-primary-base text-xs">
                              {workspace.workspace_name.charAt(0)}
                            </span>
                          </div>
                        )}
                        <span className="text-sm text-gray-700 truncate">
                          {workspace.workspace_name}
                        </span>
                      </div>
                    </button>
                  ))
                ) : (
                  <p className="text-sm text-gray-500 text-center">No team workspaces yet</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};